import { useState } from 'react'
import api from '@/lib/axios'
import type { MediaItem } from '@/types/admin'

type FilterType = 'all' | 'images' | 'documents'

export function useMediaIndexVM(initialMedia: MediaItem[]) {
  const [media, setMedia] = useState<MediaItem[]>(initialMedia)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<FilterType>('all')
  const [selected, setSelected] = useState<MediaItem | null>(null)
  const [uploading, setUploading] = useState(false)
  const [deleteId, setDeleteId] = useState<number | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [editingName, setEditingName] = useState(false)
  const [editName, setEditName] = useState('')
  const [savingName, setSavingName] = useState(false)
  const [copied, setCopied] = useState(false)

  const filtered = media.filter((item) => {
    const matchesSearch = !search || item.name.toLowerCase().includes(search.toLowerCase())
    const matchesType =
      typeFilter === 'all' ||
      (typeFilter === 'images' && item.is_image) ||
      (typeFilter === 'documents' && !item.is_image)
    return matchesSearch && matchesType
  })

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setUploading(true)
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData()
        formData.append('file', file)
        const { data } = await api.post('/admin/media', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        if (data.success) {
          setMedia((prev) => [data.media, ...prev])
        }
      }
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    try {
      await api.delete(`/admin/media/${deleteId}`)
      setMedia((prev) => prev.filter((m) => m.id !== deleteId))
      if (selected?.id === deleteId) setSelected(null)
      setDeleteId(null)
    } finally {
      setDeleting(false)
    }
  }

  const handleSaveName = async () => {
    if (!selected) return
    setSavingName(true)
    try {
      await api.patch(`/admin/media/${selected.id}`, { name: editName })
      setMedia((prev) => prev.map((m) => m.id === selected.id ? { ...m, name: editName } : m))
      setSelected((prev) => prev ? { ...prev, name: editName } : null)
      setEditingName(false)
    } finally {
      setSavingName(false)
    }
  }

  const handleCopyUrl = () => {
    if (!selected) return
    navigator.clipboard.writeText(window.location.origin + selected.url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const startEditName = () => {
    if (!selected) return
    setEditName(selected.name)
    setEditingName(true)
  }

  const cancelEditName = () => {
    setEditingName(false)
  }

  const closeDetail = () => {
    setSelected(null)
  }

  const filterTypes: FilterType[] = ['all', 'images', 'documents']
  const deleteDialogOpen = deleteId !== null

  return {
    media,
    search,
    setSearch,
    typeFilter,
    setTypeFilter,
    selected,
    setSelected,
    uploading,
    deleting,
    editingName,
    editName,
    setEditName,
    savingName,
    copied,
    filtered,
    filterTypes,
    deleteDialogOpen,
    handleUpload,
    handleDelete,
    handleSaveName,
    handleCopyUrl,
    startEditName,
    cancelEditName,
    closeDetail,
    setDeleteId,
  }
}
